import '.ao{position:relative;}.ssr-plugin'
import '.ap{flex-grow:1;}.ssr-plugin'
import { faMagnifyingGlass, faXmark } from '@fortawesome/pro-regular-svg-icons'
import { Style, styled } from 'inlines'
import { Button } from '@app/components/core/button/Button'
import { TextInput } from '@app/components/core/input/text/TextInput'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { CardWrapper } from '../card/CardWrapper'
import { Text } from '@app/components/core/typography/Text'
import { ReactNode, useCallback, useEffect, useState } from 'react'
import { useDebounce } from '@app/util/hooks/useDebounce'
import useMediaQuery from '@app/util/useMediaQuery'
import { IconButton } from '@app/components/core/button/IconButton'

export type SearchSuggestion = {
  key: string
  label: string
  sublabel?: string
  icon?: ReactNode
}

export type SuggestionsFetcher = (
  searchTerm: string,
  setSuggestions: (suggestions: SearchSuggestion[]) => void,
) => (() => void) | void

export type SearchFieldProps = {
  placeholder: string
  searchBarInput: string
  searchTerm: string
  isFocused?: boolean
  suggestionsFetcher?: SuggestionsFetcher
  setSearchBarInput: (text: string) => void
  setSearchTerm: (text: string) => void
  setIsFocused?: (value: boolean) => void
  hideClearButton?: boolean
  style?: Style
}

export const SearchField = ({
  placeholder,
  searchBarInput,
  searchTerm,
  setSearchBarInput,
  setSearchTerm,
  isFocused: isFocusedProp,
  setIsFocused: setIsFocusedProp,
  suggestionsFetcher,
  hideClearButton,
  style,
}: SearchFieldProps) => {
  const isMobile = useMediaQuery()
  const [internalFocused, setInternalFocused] = useState(false)
  const isFocused = isFocusedProp ?? internalFocused
  const setIsFocused = setIsFocusedProp ?? setInternalFocused
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([])
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      setSearchTerm(searchBarInput)
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      clearSearch()
    }
  }

  const clearSearch = () => {
    setSearchTerm('')
    setSearchBarInput('')
    setSuggestions([])
  }

  const debouncedSearchBarInput = useDebounce(searchBarInput, 200)
  const selectSuggestion = (text: string) => {
    setSearchTerm(text)
    setSuggestions([])
  }
  const performSearch = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      setSearchTerm(searchBarInput)
      setSuggestions([])
      setIsFocused(false)
    },
    [searchBarInput, setSearchTerm, setIsFocused],
  )
  useEffect(() => {
    if (!suggestionsFetcher) {
      return
    }
    if (
      debouncedSearchBarInput.trim() === '' ||
      debouncedSearchBarInput === searchTerm
    ) {
      setSuggestions([])
      return
    }
    return suggestionsFetcher(debouncedSearchBarInput, setSuggestions)
  }, [debouncedSearchBarInput, searchTerm, suggestionsFetcher])

  return (
    <styled.div className="ao ap"
      style={{
        ...style,
      }}
    >
      <TextInput
        text={searchBarInput}
        setText={setSearchBarInput}
        placeholder={placeholder}
        icon={faMagnifyingGlass}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onKeyDown={handleKeyDown}
        containerStyle={{
          borderRadius: '1.875rem',
          padding: '0.25rem 0.25rem 0.25rem 1rem',
          background: 'var(--search-input-bg)',
        }}
        endAdornment={
          <>
            {searchTerm.length > 0 && !hideClearButton && (
              <IconButton
                onClick={clearSearch}
                icon={faXmark}
                variant="transparent"
                aria-label="Clear search"
                style={{
                  border: 0,
                  color: 'var(--palette-blue-200)',
                  '&:hover': { color: 'var(--brand-secondary)' },
                }}
              />
            )}
            {(!isMobile || isFocused) && (
              <Button label="Zoek" onMouseDown={performSearch} />
            )}
          </>
        }
      />

      {suggestions.length > 0 && (
        <CardWrapper
          style={{
            position: 'absolute',
            top: 'calc(100% + 0.5rem)',
            width: '100%',
          }}
        >
          <Column gap="0">
            {suggestions.map((suggestion) => (
              <Suggestion
                key={suggestion.key}
                suggestion={suggestion}
                onSelect={selectSuggestion}
              />
            ))}
          </Column>
        </CardWrapper>
      )}
    </styled.div>
  )
}

type SuggestionProps = {
  suggestion: SearchSuggestion
  onSelect: (label: string) => void
}

const Suggestion = ({ suggestion, onSelect }: SuggestionProps) => {
  return (
    <div onClick={() => onSelect(suggestion.label)}>
      <Row gap="0.5rem" style={{ cursor: 'pointer', padding: '1rem' }}>
        {suggestion.icon}

        <Column gap="0.25rem">
          <Text.Medium isBold>{suggestion.label}</Text.Medium>
          {suggestion.sublabel && (
            <Text.Small>{suggestion.sublabel}</Text.Small>
          )}
        </Column>
      </Row>
    </div>
  )
}
